import { useLayoutEffect, useRef } from 'react';
import { DropSelect } from '@zawarski/eti-ui';

import { PocNotificationRoleOptions } from 'common/constants';
import { PocNotificationRole } from 'common/enum';
import { cn } from '@/lib/utils';

const ROLE_VALUES: string[] = Object.values(PocNotificationRole);

/**
 * eti-ui types `DropSelect`'s `onChange` as `(value: any) => void`, so nothing about the value
 * arriving here is checked. Narrow it by testing membership rather than asserting it: the
 * clear button emits `''`, and a widened option list would otherwise reach form state as a
 * role the column does not accept.
 */
const toRole = (value: string): Nullable<PocNotificationRoleValue> =>
  ROLE_VALUES.includes(value) ? (value as PocNotificationRoleValue) : null;

interface Props {
  /**
   * What the control announces. The same words are already on screen in the row's Event cell,
   * so this is not repeated visually -- see the naming note below.
   */
  label: string;
  value: Nullable<PocNotificationRoleValue>;
  onChange: (role: Nullable<PocNotificationRoleValue>) => void;
  /**
   * Viewing rather than editing. eti-ui drops `onValueChange`, pins the menu shut and stops
   * rendering the clear button, so the role stays readable and stays put. Deliberately not
   * `disabled`, which would dim the chip and drop the control out of the tab order -- a role a
   * viewer cannot reach is a role they cannot read.
   */
  readOnly?: boolean;
}

/*
 * The Type control for one row of the contact modal's notification table.
 *
 *   <td>  Event cell    [x] Bed count updates        <- the visible name
 *   <td>  Type cell     <RoleSelect label='Bed count updates' />
 *                         `-- <button role='combobox' aria-label>   <- written by the effect
 *                         `-- <button aria-label='Clear ...'>       <- only when a role is set
 *
 * Named through `aria-label` rather than eti-ui's `label` prop on purpose. Passing `label`
 * renders a visible caption stacked above the control, which in a two-column table duplicates
 * the `Type` column header on every row. The row is already named by its Event cell; this
 * carries that name to assistive tech without repeating it on screen.
 *
 * eti-ui's `DropSelect` takes a closed set of props with no rest-spread, so nothing a caller
 * passes reaches the trigger. The name is applied to the rendered node instead, the same way
 * `components/ui/drop-select.tsx` repairs that component's `htmlFor`.
 *
 * Coverage:
 *   no role            -> placeholder text, no chip, no clear button
 *   role set           -> chip styling, clear button named after the row
 *   trigger swapped    -> re-applied every render, see the dependency note below
 *   cleared            -> eti-ui emits '', mapped back to null at the boundary below
 */
function RoleSelect({ label, value, onChange, readOnly = false }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Scoped to the two things that can undo this work. With `showClearButton` set, `DropSelect`
  // renders a raw `SelectPrimitive.Trigger` once a value is selected and a `SelectTrigger`
  // otherwise. Those are different component types, so React discards the node this effect
  // wrote whenever the role crosses between set and unset -- the same `value` transition the
  // remount key below keys on. Re-running on `[value, label]` therefore covers every occasion
  // the swap can happen, without repeating two DOM queries on unrelated renders.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const trigger = container.querySelector<HTMLElement>('[role="combobox"]');
    // Guarded rather than written unconditionally: this runs on every render, and rewriting an
    // attribute to the value it already holds still costs a mutation record and an
    // accessible-name recomputation. After the first render the steady state is read-only.
    if (trigger && trigger.getAttribute('aria-label') !== label) {
      trigger.setAttribute('aria-label', label);
    }

    // eti-ui hardcodes this button's name to 'Clear selection'. With four rows in the table
    // that is four identically-named buttons and no way to tell which row each one belongs to.
    const clearButton = container.querySelector<HTMLElement>('button[aria-label^="Clear"]');
    const clearLabel = `Clear ${label}`;
    if (clearButton && clearButton.getAttribute('aria-label') !== clearLabel) {
      clearButton.setAttribute('aria-label', clearLabel);
    }
  }, [value, label]);

  return (
    <div ref={containerRef}>
      <DropSelect
        /*
         * Remount the control when the role crosses between set and unset, so Radix cannot read
         * back a role the user has already cleared.
         *
         * `DropSelect` narrows its own value to `options.some(o => o.value === value) ? value :
         * undefined` before handing it to Radix's `Select`, and Radix's `useControllableState`
         * reads `undefined` as "uncontrolled" and falls back to its own internal state. So an
         * empty row starts uncontrolled, the user's first pick seeds Radix's internal state, and
         * clearing the role hands back `undefined` -- at which point Radix resumes reading that
         * stale internal value and the trigger keeps rendering the old role. The checkbox reads
         * unchecked and form state reads `null` while the Type cell still says PRIMARY, which is
         * a contact that looks subscribed and is not.
         *
         * Passing `''` instead of `undefined` does not help: it is not one of the options either,
         * so `DropSelect` narrows it to `undefined` just the same. Discarding the subtree is what
         * actually clears the internal state. Only the null-vs-set transition changes the key, so
         * switching between two roles does not remount -- Radix is controlled throughout that
         * transition and needs no reset.
         */
        key={value === null ? 'no-role' : 'role'}
        showClearButton
        readOnly={readOnly}
        options={PocNotificationRoleOptions}
        placeholder={'Select notification'}
        // The design's own copy disagrees between rows -- 'Select notification' on the first
        // and 'Select notifications' on the third. Singular, because the control holds one.
        value={value ?? undefined}
        // Anything that is not one of the three roles means "no role", which is `null` on the
        // wire -- `undefined` would be dropped by `JSON.stringify` rather than sent.
        onChange={(next: string) => onChange(toRole(next))}
        triggerClassName={cn(
          // eti-ui pins the trigger to `h-9` through `data-[size=default]`, an attribute only
          // its own `SelectTrigger` wrapper writes. The clear-button variant it swaps in once a
          // role is set is a raw `SelectPrimitive.Trigger` with no such attribute, so that one
          // falls back to its content and lands 2px shorter -- the row nudges every time a role
          // is picked or cleared. Pinned here so the control keeps one height across both
          // variants, and so it matches the checkbox in the Event cell beside it.
          'hc:h-9',
          // The value slot is `flex-1` in eti-ui's base classes, which would stretch the chip
          // background across the whole trigger. `flex-none` lets it hug its text.
          value &&
            'hc:*:data-[slot=select-value]:flex-none hc:*:data-[slot=select-value]:rounded-xs hc:*:data-[slot=select-value]:bg-background-muted hc:dark:*:data-[slot=select-value]:bg-muted hc:*:data-[slot=select-value]:px-1.5 hc:*:data-[slot=select-value]:text-xs hc:*:data-[slot=select-value]:font-semibold hc:*:data-[slot=select-value]:tracking-wide hc:*:data-[slot=select-value]:text-typography-accent hc:*:data-[slot=select-value]:uppercase'
        )}
      />
    </div>
  );
}

export { RoleSelect };
